import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Btn } from "@/components/kit";
import { BRAND } from "@/config/brand";
import { useProgress } from "@/modules/premiere-video/store";
import { ANGLES, STORYBOARD, PREP_CHECKLIST } from "@/modules/premiere-video/content";
import { assembleScript, filled, scriptStats } from "@/modules/premiere-video/logic";

const PARTS = [["hook", "Accroche"], ["demo", "Démonstration"], ["conclusion", "Conclusion"]];
const Box = () => <span className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded border border-[#C6A36A]" aria-hidden />;

export default function ScriptPrint() {
  const { state } = useProgress();
  const { m2, m3, m4, m5 } = state;
  const script = assembleScript(m4);
  const st = scriptStats(script);
  const angle = ANGLES.find((a) => a.id === m3.angle);
  const today = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#EFE9E3] print:bg-white" data-testid="script-print-page">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-ivory/95 px-5 py-3 backdrop-blur sm:px-8">
        <Link to="/mission/4" className="flex items-center gap-2 text-sm text-bx" data-testid="script-print-back"><ArrowLeft className="h-4 w-4" />Retour</Link>
        <Btn size="sm" onClick={() => window.print()} data-testid="script-print-button"><Printer className="h-4 w-4" />Imprimer / PDF</Btn>
      </div>
      <article className="mx-auto my-8 max-w-[820px] bg-white px-8 py-10 shadow-[0_20px_60px_-30px_rgba(36,33,39,0.35)] sm:px-14 sm:py-14 print:my-0 print:max-w-none print:px-0 print:py-0 print:shadow-none">
        <header className="flex items-start justify-between gap-6 border-b-2 border-bx pb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9A7A45]">Mon script UGC</p>
            <h1 className="mt-2 text-3xl font-semibold text-bx sm:text-4xl" data-testid="script-print-title">{filled(m2.name) ? m2.name : "Ma première vidéo UGC"}</h1>
            <p className="mt-2 text-sm text-soft">{angle ? `Angle ${angle.label.toLowerCase()} · ` : ""}{st.words} mots · environ {st.seconds} s · {today}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <img src={BRAND.logoMark} alt="" className="h-10 w-10 rounded-lg" />
            <span className="hidden text-sm font-bold tracking-[0.14em] text-bx sm:inline">UGC GO</span>
          </div>
        </header>

        {filled(m3.idea) && (
          <p className="mt-6 rounded-xl bg-ivory px-5 py-4 font-display text-lg italic text-bx print:border print:border-line">« {m3.idea} »</p>
        )}

        <section className="mt-8 space-y-7">
          {script ? PARTS.map(([k, label], i) => filled(m4[k]) && (
            <div key={k} className="grid gap-3 sm:grid-cols-[110px_1fr]" style={{ breakInside: "avoid" }}>
              <div>
                <p className="font-display text-3xl text-gold">0{i + 1}</p>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-bx-2">{label}</p>
              </div>
              <p className="whitespace-pre-line text-xl leading-relaxed text-ink" data-testid={`script-print-${k}`}>{m4[k]}</p>
            </div>
          )) : <p className="italic text-soft" data-testid="script-print-empty">Ton script est encore vide : complète la mission 04.</p>}
        </section>

        <section className="mt-10 border-t border-line pt-7" style={{ breakInside: "avoid" }}>
          <h2 className="text-xl font-medium text-bx">Mes plans à filmer</h2>
          <ol className="mt-4 grid gap-3 sm:grid-cols-2">
            {STORYBOARD.map((p) => (
              <li key={p.id} className="flex gap-3 rounded-xl border border-line p-4">
                <Box />
                <div>
                  <p className="font-display text-bx">{p.title}</p>
                  <p className="mt-1 text-sm text-ink/85">{filled(m5.notes[p.id]) ? m5.notes[p.id] : p.action}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-8" style={{ breakInside: "avoid" }}>
          <h2 className="text-xl font-medium text-bx">Avant d'appuyer sur « enregistrer »</h2>
          <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
            {PREP_CHECKLIST.map((it) => <li key={it.id} className="flex gap-2.5 text-sm text-ink"><Box />{it.label}</li>)}
          </ul>
        </section>

        <footer className="mt-12 flex items-center justify-between border-t border-line pt-5 text-xs text-soft">
          <span className="font-display italic text-bx">{BRAND.tagline}</span>
          <span>Parle naturellement : ce script est un guide, pas un texte à réciter.</span>
        </footer>
      </article>
    </div>
  );
}
